import React from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Text,
  VStack,
  HStack,
  Box,
  Icon,
  keyframes,
  Flex,
} from "@chakra-ui/react";
import { AlertTriangle, Trash2, X } from "lucide-react";

interface DeleteConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  itemName: string;
  itemType?: string;
  description?: string;
  isDeleting?: boolean;
  warningMessage?: string;
  confirmButtonText?: string;
  cancelButtonText?: string;
}

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(20px) scale(0.95); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

const scaleIn = keyframes`
  from { transform: scale(0); }
  to { transform: scale(1); }
`;

const DeleteConfirmationModal: React.FC<DeleteConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title = "Delete Confirmation",
  itemName,
  itemType = "item",
  description,
  isDeleting = false,
  warningMessage = "This action will soft delete the item. It can be restored by an administrator if needed.",
  confirmButtonText = "Delete",
  cancelButtonText = "Cancel",
}) => {
  const handleConfirm = () => {
    onConfirm();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      size="md"
      motionPreset="slideInBottom"
    >
      <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(10px)" />
      <ModalContent
        borderRadius="3xl"
        overflow="hidden"
        boxShadow="dark-lg"
        mx={4}
        animation={`${slideUp} 0.3s cubic-bezier(0.4, 0, 0.2, 1)`}
        border="1px solid"
        borderColor="red.100"
      >
        {/* Header - Compact Design */}
        <ModalHeader p={0} position="relative">
          <Flex align="center" justify="space-between" px={6} pt={5} pb={4}>
            {/* Icon with animated background */}
            <Flex align="center" gap={3}>
              <Box
                position="relative"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Box
                  position="absolute"
                  w="48px"
                  h="48px"
                  bg="red.100"
                  borderRadius="full"
                  opacity={0.6}
                  animation={`${scaleIn} 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)`}
                />
                <Box
                  bg="red.500"
                  borderRadius="full"
                  p={2.5}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  position="relative"
                  boxShadow="0 4px 14px 0 rgba(239, 68, 68, 0.4)"
                  animation={`${scaleIn} 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)`}
                >
                  <Icon as={AlertTriangle} w={5} h={5} color="white" />
                </Box>
              </Box>
              <Box>
                <Text
                  fontSize="lg"
                  fontWeight="700"
                  color="gray.800"
                  letterSpacing="tight"
                >
                  {title}
                </Text>
                <Text fontSize="xs" color="gray.500" fontWeight="500">
                  This action requires confirmation
                </Text>
              </Box>
            </Flex>

            {/* Close button */}
            <Box
              as="button"
              onClick={onClose}
              p={1.5}
              borderRadius="lg"
              transition="all 0.2s"
              _hover={{ bg: "gray.100" }}
              _active={{ bg: "gray.200" }}
              disabled={isDeleting}
            >
              <Icon as={X} w={5} h={5} color="gray.400" />
            </Box>
          </Flex>
        </ModalHeader>

        {/* Body - Compact & Clean */}
        <ModalBody px={6} py={4}>
          <VStack spacing={3} align="stretch">
            {/* Main Message */}
            <Text fontSize="sm" color="gray.600" fontWeight="500">
              Are you sure you want to delete this {itemType}?
            </Text>

            {/* Item Name - Sleek Design */}
            <Box
              bg="gradient-to-r"
              bgGradient="linear(to-r, red.50, red.100)"
              borderRadius="xl"
              p={3.5}
              border="1px solid"
              borderColor="red.200"
              position="relative"
              overflow="hidden"
            >
              <Box
                position="absolute"
                top={0}
                right={0}
                bottom={0}
                left={0}
                bgGradient="linear(to-br, transparent, red.50)"
                opacity={0.5}
              />
              <HStack spacing={3} position="relative" zIndex={1}>
                <Box bg="white" borderRadius="lg" p={2} boxShadow="sm">
                  <Icon as={Trash2} w={4} h={4} color="red.600" />
                </Box>
                <Box flex={1} minW={0}>
                  <Text
                    fontSize="10px"
                    color="red.600"
                    fontWeight="700"
                    letterSpacing="wide"
                    mb={0.5}
                  >
                    {itemType.toUpperCase()}
                  </Text>
                  <Text
                    fontSize="sm"
                    fontWeight="700"
                    color="red.900"
                    noOfLines={1}
                    wordBreak="break-all"
                  >
                    {itemName}
                  </Text>
                </Box>
              </HStack>
            </Box>

            {/* Custom Description - Only if provided */}
            {description && (
              <Box
                bg="orange.50"
                borderLeft="3px solid"
                borderColor="orange.400"
                borderRadius="lg"
                p={3}
              >
                <Text
                  fontSize="xs"
                  color="orange.900"
                  lineHeight="1.6"
                  fontWeight="500"
                >
                  {description}
                </Text>
              </Box>
            )}

            {/* Warning Message - Minimal */}
            <Box
              bg="gray.50"
              borderRadius="lg"
              p={3}
              border="1px solid"
              borderColor="gray.200"
            >
              <Text fontSize="xs" color="gray.600" lineHeight="1.6">
                {warningMessage}
              </Text>
            </Box>
          </VStack>
        </ModalBody>

        {/* Footer - Modern Button Layout */}
        <ModalFooter
          px={6}
          py={4}
          bg="gray.50"
          borderTop="1px solid"
          borderColor="gray.100"
        >
          <HStack spacing={3} w="full">
            <Button
              flex={1}
              variant="ghost"
              size="md"
              onClick={onClose}
              isDisabled={isDeleting}
              fontSize="sm"
              fontWeight="600"
              h="44px"
              borderRadius="xl"
              color="gray.700"
              _hover={{
                bg: "gray.200",
              }}
              _active={{
                bg: "gray.300",
              }}
            >
              {cancelButtonText}
            </Button>
            <Button
              flex={1}
              size="md"
              onClick={handleConfirm}
              isLoading={isDeleting}
              loadingText="Deleting..."
              fontSize="sm"
              fontWeight="700"
              h="44px"
              borderRadius="xl"
              bg="red.500"
              color="white"
              leftIcon={<Icon as={Trash2} w={4} h={4} />}
              _hover={{
                bg: "red.600",
                transform: "translateY(-1px)",
                boxShadow: "0 8px 16px -4px rgba(239, 68, 68, 0.4)",
              }}
              _active={{
                transform: "translateY(0)",
                bg: "red.700",
              }}
              transition="all 0.2s cubic-bezier(0.4, 0, 0.2, 1)"
              boxShadow="0 4px 12px -2px rgba(239, 68, 68, 0.3)"
            >
              {confirmButtonText}
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default DeleteConfirmationModal;
